// useRemoteRunner — runs phone-dispatched agent tasks on THIS machine.
//
// Sibling of useLocalScheduler. While the app is open + authed, polls
// /api/dispatch/pending, claims a dispatch, and runs it via the EXISTING
// runLocalAgentLoop — passing a remote event sink (POST /events) and a remote
// approver (POST approval_request + poll the phone's decision). Streams back
// to the cloud so the phone watches live and approves inline.
//
// Transport is outbound-only HTTPS (poll + POST). Nothing inbound, no tunnel.

import { useEffect, useRef } from 'react';
import * as remoteClient from '../lib/remoteDispatchClient.js';
import { apiUrl } from '../lib/api.js';
import { runLocalAgentLoop } from '../lib/agentLoop.js';
import { defaultToolPolicy } from '../lib/toolPolicy.js';
import {
  isGithubMentionDispatch,
  buildCloneUrl,
  buildBranchName,
  buildWorkingDir,
  splitRepoFullName,
  buildPrTitle,
  buildPrBody,
  buildResultComment,
  createPullRequest as ghCreatePullRequest,
  postIssueComment as ghPostIssueComment,
  reactDone as ghReactDone,
} from '../lib/githubMention.js';
import { hermesApi as realHermesApi } from '../hermes/infrastructure/hermesApiClient.js';
import { buildHermesDriveRun } from '../lib/runtime/hermesRunWiring.js';

const POLL_INTERVAL_MS = 4_000;
const APPROVAL_POLL_MS = 1_500;
const CANCEL_POLL_MS = 2_000;

function defaultSleep(ms, signal) {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve();
    const t = setTimeout(resolve, ms);
    signal?.addEventListener?.(
      'abort',
      () => {
        clearTimeout(t);
        resolve();
      },
      { once: true },
    );
  });
}

function genRequestId(dispatchId) {
  const rand = Math.random().toString(16).slice(2, 8);
  return `req-${dispatchId}-${Date.now()}-${rand}`;
}

/** Watch for a client-requested cancel and abort the controller when seen. */
async function watchCancel(client, id, controller, sleep, cancelPollMs) {
  while (!controller.signal.aborted) {
    let full = null;
    try {
      full = await client.getDispatch(id);
    } catch {
      full = null;
    }
    if (full?.dispatch?.cancel_req) {
      controller.abort();
      return;
    }
    await sleep(cancelPollMs, controller.signal);
  }
}

// Shell run that returns the IPC result; never throws (defensive for the flow).
async function runShell(shell, command, opts = {}) {
  try {
    return await shell.exec({ command, ...opts });
  } catch (e) {
    return { ok: false, exitCode: 1, stderr: e?.message || String(e) };
  }
}

/**
 * The @apolo GitHub-mention executor. A dispatch with
 * context.source==='github_mention' carries everything we need to:
 *   clone (via the install token) → run the agent on the instruction →
 *   commit → push a branch → open a PR → comment back (PR link + ✅ reaction).
 *
 * Reuses the same remote event sink + completion path as runDispatch so the
 * phone/cloud watches progress live. The install token is LOCAL-ONLY: it is
 * used to build the clone remote + the GitHub REST calls and is NEVER posted to
 * an event or logged.
 *
 * deps (injectable for tests): {
 *   client, prepareRun, runLoop, sleep, cancelPollMs,
 *   shell: { exec }, git: { commit, push, status },
 *   gh: { createPullRequest, postIssueComment, reactDone }, tmpRoot
 * }
 * Exported for testing.
 */
export async function runGithubMentionDispatch(dispatch, deps) {
  const {
    client = remoteClient,
    prepareRun,
    runLoop = runLocalAgentLoop,
    sleep = defaultSleep,
    cancelPollMs = CANCEL_POLL_MS,
    shell = typeof window !== 'undefined' ? window.apolo?.shell : undefined,
    git = typeof window !== 'undefined' ? window.apolo?.git : undefined,
    gh = {
      createPullRequest: ghCreatePullRequest,
      postIssueComment: ghPostIssueComment,
      reactDone: ghReactDone,
    },
    tmpRoot,
  } = deps || {};

  const id = dispatch.id;
  const ctx = dispatch.context || {};
  const {
    repoFullName,
    issueNumber,
    instruction,
    installationToken,
    baseBranch,
    commentId,
    author,
  } = ctx;

  const claimed = await client.claim(id);
  if (!claimed.ok) return { skipped: true };

  const controller = new AbortController();
  const cancelWatch = watchCancel(client, id, controller, sleep, cancelPollMs);

  let flush = Promise.resolve();
  const postEvent = (type, payload) => {
    flush = flush.then(() => client.postEvents(id, [{ type, payload }]).catch(() => {}));
  };

  // Token-safe event sink for the agent loop (same shape as runDispatch).
  const onEvent = (evt) => {
    const { type, ...payload } = evt;
    postEvent(type, payload);
    if (type === 'tool_call' && evt.name === 'write_file' && evt.args) {
      postEvent('diff', { file: evt.args.path || evt.args.file || '', content: evt.args.content || '' });
    }
  };

  const fail = async (message) => {
    postEvent('error', { error: message });
    // Best-effort: tell the human something went wrong on the thread. Bot-marked
    // so the webhook's anti-loop guard ignores it.
    try {
      await gh.postIssueComment({
        token: installationToken,
        repoFullName,
        issueNumber,
        body: `⚠️ Apolo encontrou um erro ao processar essa solicitação: ${message}\n\n<sub>🤖 posted by @apolo[bot]</sub>`,
      });
    } catch { /* non-fatal */ }
    await flush;
    await client.complete(id, { status: 'failed', error: message });
    controller.abort();
    await cancelWatch;
    return { ok: false, finalText: '', stopReason: 'github_mention_failed' };
  };

  try {
    if (!installationToken) return await fail('missing installation token');
    if (!repoFullName) return await fail('missing repoFullName');

    const workingDir = buildWorkingDir({ dispatchId: id, ...(tmpRoot ? { tmpRoot } : {}) });
    const branch = buildBranchName({ issueNumber, instruction });
    const base = baseBranch || 'main';
    const cloneUrl = buildCloneUrl({ installationToken, repoFullName });

    // 1) Clone (fresh: nuke any stale dir from a previous run of this id, then
    //    clone, then checkout base + create the work branch). Token-bearing
    //    command — never posted to an event.
    postEvent('assistant', { content: `Clonando ${repoFullName} (#${issueNumber})…` });
    const clone = await runShell(
      shell,
      `rm -rf ${workingDir} && git clone ${cloneUrl} ${workingDir}`,
    );
    if (!clone.ok) {
      return await fail(`clone failed (exit ${clone.exitCode ?? '?'}): ${clone.stderr || ''}`.trim());
    }
    // checkout base (best-effort — a fresh clone is already on the default) +
    // create the working branch.
    await runShell(shell, `git checkout ${base}`, { cwd: workingDir });
    const co = await runShell(shell, `git checkout -b ${branch}`, { cwd: workingDir });
    if (!co.ok) {
      return await fail(`branch create failed: ${co.stderr || ''}`.trim());
    }

    // 2) Run the agent on the cloned dir, with the instruction as the user msg.
    const prep = (await prepareRun?.(dispatch)) || {};
    const agentResult = await runLoop({
      chatFn: prep.chatFn,
      baseUrl: prep.baseUrl,
      model: prep.model,
      options: prep.options || {},
      root: workingDir,
      allowSendToUser: true, // A7 (E5) — headless run: expose the verbatim-delivery tool here
      toolPolicy: defaultToolPolicy(), // E0 — deny secret dirs even in autonomous runs
      messages: [{ role: 'user', content: instruction || dispatch.prompt || '' }],
      permissionsMode: 'ignore', // github-mention runs autonomously (no phone approver)
      onEvent,
      signal: controller.signal,
    });
    const summary = agentResult?.finalText || '';

    // 3) Did the agent change anything? (working-tree status under the clone)
    let changed = true;
    try {
      const st = await git.status({ root: workingDir });
      changed = !!(st?.ok && Array.isArray(st.files) && st.files.length > 0);
    } catch {
      changed = true; // be permissive — let commit decide if there's nothing
    }

    if (!changed) {
      postEvent('assistant', { content: 'Nenhuma mudança a commitar.' });
      await gh.postIssueComment({
        token: installationToken,
        repoFullName,
        issueNumber,
        body: buildResultComment({ noChanges: true }),
      });
      await gh.reactDone({ token: installationToken, repoFullName, commentId });
      postEvent('complete', { status: 'completed', result: summary });
      await flush;
      await client.complete(id, { status: 'completed', result: summary });
      controller.abort();
      await cancelWatch;
      return { ok: true, finalText: summary, stopReason: 'no_changes', prUrl: null };
    }

    // 4) Commit + push the branch (token-bearing remote already set by clone).
    const commit = await git.commit({
      root: workingDir,
      message: `${buildPrTitle({ instruction, issueNumber })}\n\nCloses #${issueNumber}`,
      addAll: true,
    });
    if (commit && commit.ok === false && commit.reason === 'nothing_to_commit') {
      // status said changed but commit found nothing (e.g. only ignored files)
      await gh.postIssueComment({
        token: installationToken,
        repoFullName,
        issueNumber,
        body: buildResultComment({ noChanges: true }),
      });
      await gh.reactDone({ token: installationToken, repoFullName, commentId });
      postEvent('complete', { status: 'completed', result: summary });
      await flush;
      await client.complete(id, { status: 'completed', result: summary });
      controller.abort();
      await cancelWatch;
      return { ok: true, finalText: summary, stopReason: 'no_changes', prUrl: null };
    }

    const push = await git.push({ root: workingDir, branch, setUpstream: true });
    if (push && push.ok === false) {
      return await fail(`push failed: ${push.detail || ''}`.trim());
    }

    // 5) Open the PR (head=branch, base) via the install token.
    const { owner, repo } = splitRepoFullName(repoFullName);
    const pr = await gh.createPullRequest({
      token: installationToken,
      owner,
      repo,
      base,
      head: branch,
      title: buildPrTitle({ instruction, issueNumber }),
      body: buildPrBody({ issueNumber, instruction, summary, author }),
    });
    if (!pr?.ok) {
      return await fail(`PR open failed: ${pr?.error || 'unknown'}`);
    }
    const prUrl = pr.pull_request?.html_url || null;

    // 6) Comment back (PR link + summary, bot-marked) + ✅ reaction.
    await gh.postIssueComment({
      token: installationToken,
      repoFullName,
      issueNumber,
      body: buildResultComment({ prUrl, summary }),
    });
    await gh.reactDone({ token: installationToken, repoFullName, commentId });

    postEvent('complete', { status: 'completed', result: prUrl || summary });
    await flush;
    await client.complete(id, { status: 'completed', result: prUrl || summary });
    controller.abort();
    await cancelWatch;
    return { ok: true, finalText: summary, stopReason: 'pr_opened', prUrl };
  } catch (e) {
    return await fail(e?.message || String(e));
  }
}

/** A Hermes scheduled-run dispatch (P3-T03b, D19): the backend enqueued it when
 * a schedule fired while this Mac was online, so we drive the run WITH tools +
 * memory + worktree (not the toolless cloud loop). */
export function isHermesRunDispatch(dispatch) {
  return dispatch?.context?.source === 'hermes_run';
}

// Lazily build the production driveRun ONCE (the RunAgent wiring is stateless;
// rebuilding it per dispatch would be wasteful). Tests inject `driveRun`, so
// this is never reached under test.
let _hermesDriveRun = null;
function defaultHermesDriveRun(...args) {
  if (!_hermesDriveRun) _hermesDriveRun = buildHermesDriveRun();
  return _hermesDriveRun(...args);
}

/**
 * Execute a Hermes scheduled-run dispatch on this machine (P3-T03b, D19).
 *
 * When a Hermes schedule fires while the Mac is online, the backend (T03a)
 * enqueues a remote_dispatch carrying context.source==='hermes_run' +
 * { hermesRunId, agentId } — the hermes_run row ALREADY exists (queued). This
 * wrapper:
 *   claim the DISPATCH ticket → fetch the agent + run → driveRun (the SAME
 *   .app driver launchRun uses: claim CAS queued→running→done/failed, tools +
 *   memory + worktree) → complete the DISPATCH ticket.
 *
 * Separation of concerns: the hermes_run state (running/done/failed/result) is
 * owned by driveRun's lifecycle PATCHes to hermes_runs. THIS wrapper only owns
 * the dispatch queue ticket (claim/complete) — it never PATCHes hermes_runs.
 *
 * deps (injectable for tests): { client, hermesApi, driveRun, getAgentRun?, sleep? }.
 * Never throws — a crash is caught and best-effort completes the ticket failed.
 * Exported for testing.
 */
export async function runHermesRunDispatch(dispatch, deps) {
  const {
    client = remoteClient,
    hermesApi = realHermesApi,
    driveRun = defaultHermesDriveRun,
  } = deps || {};

  const id = dispatch.id;

  // Claim the dispatch ticket (same semantics as runDispatch: a 409/already-
  // claimed means another runner won the race — skip silently).
  const claimed = await client.claim(id);
  if (!claimed.ok) return { skipped: true };

  try {
    const { hermesRunId, agentId } = dispatch.context || {};

    // Fetch the agent + the already-created (queued) run.
    const agent = await hermesApi.getAgent(agentId);
    const detail = await hermesApi.getRun(hermesRunId);
    const run = detail?.run;
    if (!agent || !run) {
      await client.complete(id, { status: 'failed', error: 'hermes_run_or_agent_missing' });
      return { ok: false };
    }

    // Drive the run. The dispatch cancel path aborts the controller; driveRun
    // already maps an aborted loop → the hermes_run 'cancelled' state.
    const controller = new AbortController();
    const out = await driveRun(run, agent, { signal: controller.signal });

    // Multi-channel outbound (P4-T02, D20). The run is now TERMINAL in
    // hermes_runs. If it came from a channel, ask the backend to post the result
    // back — the Mac CANNOT decrypt the channel secret, so this is the seam that
    // closes the channel loop for Mac-driven runs (the cloud loop has its own).
    // Best-effort: a failure here must NEVER fail the dispatch (the run already
    // succeeded), and a non-channel run answers 200 {posted:false} harmlessly.
    if (typeof hermesApi.requestOutbound === 'function') {
      await hermesApi
        .requestOutbound(hermesRunId)
        .catch((e) => console.warn('[hermes] outbound failed', e));
    }

    // Complete the DISPATCH ticket per the run outcome. driveRun ALREADY wrote
    // the authoritative hermes_run state — we only close the queue ticket.
    const failed = out?.result?.ok === false;
    await client.complete(id, {
      status: failed ? 'failed' : 'completed',
      result: out?.run?.result ?? '',
      error: failed ? (out.result.error || '') : null,
    });
    return { ok: true, runId: hermesRunId };
  } catch (e) {
    // Never propagate — best-effort close the ticket so it doesn't hang.
    await client.complete(id, { status: 'failed', error: e?.message || String(e) }).catch(() => {});
    return { ok: false };
  }
}

/**
 * Claim + run a single dispatch on this machine. Exported for testing.
 * deps: { client, prepareRun, runLoop?, sleep?, approvalPollMs?, cancelPollMs?, githubFlow? }
 *   prepareRun(dispatch) -> { chatFn, baseUrl, model, root, options?, permissionsMode? }
 */
export async function runDispatch(dispatch, deps) {
  const {
    client = remoteClient,
    prepareRun,
    runLoop = runLocalAgentLoop,
    sleep = defaultSleep,
    approvalPollMs = APPROVAL_POLL_MS,
    cancelPollMs = CANCEL_POLL_MS,
    githubFlow = runGithubMentionDispatch,
    hermesFlow = runHermesRunDispatch,
  } = deps || {};

  // @apolo GitHub-mention dispatches take a dedicated clone→agent→PR→comment
  // path. Hermes scheduled-run dispatches take the Hermes driver (tools +
  // memory + worktree). Everything else (phone/desktop) runs the normal loop.
  if (isGithubMentionDispatch(dispatch)) {
    return githubFlow(dispatch, { client, prepareRun, runLoop, sleep, cancelPollMs });
  }
  if (isHermesRunDispatch(dispatch)) {
    return hermesFlow(dispatch, { client });
  }

  const id = dispatch.id;
  const claimed = await client.claim(id);
  if (!claimed.ok) return { skipped: true };

  const controller = new AbortController();
  const cancelWatch = watchCancel(client, id, controller, sleep, cancelPollMs);

  // Serialized event flush — keeps event order without blocking the loop.
  let flush = Promise.resolve();
  const postEvent = (type, payload) => {
    flush = flush.then(() => client.postEvents(id, [{ type, payload }]).catch(() => {}));
  };

  const onEvent = (evt) => {
    // The loop emits assistant/tool_call/tool_result/done/error/aborted/max_iterations.
    const { type, ...payload } = evt;
    postEvent(type, payload);
    // E4: write_file → também emite um evento `diff` pro celular renderizar/aprovar.
    if (type === 'tool_call' && evt.name === 'write_file' && evt.args) {
      postEvent('diff', { file: evt.args.path || evt.args.file || '', content: evt.args.content || '' });
    }
  };

  const requestApproval = async (desc) => {
    const requestId = genRequestId(id);
    await client.postEvents(id, [{ type: 'approval_request', payload: { request_id: requestId, desc } }]);
    for (;;) {
      if (controller.signal.aborted) return false;
      let decision;
      try {
        decision = await client.pollApproval(id, requestId, controller.signal);
      } catch {
        decision = null;
      }
      if (decision?.decided) return !!decision.approved;
      await sleep(approvalPollMs, controller.signal);
    }
  };

  let result = { ok: false, finalText: '', stopReason: 'unknown' };
  try {
    const prep = (await prepareRun(dispatch)) || {};
    result = await runLoop({
      chatFn: prep.chatFn,
      baseUrl: prep.baseUrl,
      model: prep.model,
      options: prep.options || {},
      root: prep.root,
      allowSendToUser: true, // A7 (E5) — headless run: expose the verbatim-delivery tool here
      toolPolicy: defaultToolPolicy(), // E0 — deny secret dirs even in autonomous runs
      messages: [{ role: 'user', content: dispatch.prompt }],
      permissionsMode: prep.permissionsMode || (dispatch.autonomy === 'auto' ? 'ignore' : 'ask'),
      requestApproval,
      onEvent,
      signal: controller.signal,
    });
    const status = result?.ok ? 'completed' : 'failed';
    postEvent('complete', { status, result: result?.finalText || '' });
    await flush;
    await client.complete(id, {
      status,
      result: result?.finalText || '',
      error: result?.ok ? null : result?.stopReason || 'failed',
    });
  } catch (e) {
    postEvent('error', { error: e?.message || String(e) });
    await flush;
    await client.complete(id, { status: 'failed', error: e?.message || String(e) });
    result = { ok: false, finalText: '', stopReason: 'exception' };
  } finally {
    controller.abort(); // stop the cancel watcher
    await cancelWatch;
  }
  return result;
}

/**
 * Hook: while `enabled`, poll for pending dispatches and run them locally.
 * prepareRun(dispatch) -> run config (chatFn/baseUrl/model/root/permissionsMode).
 */
export function useRemoteRunner({ enabled = false, prepareRun, pollMs = POLL_INTERVAL_MS } = {}) {
  const runningRef = useRef(new Set());
  const prepareRef = useRef(prepareRun);
  useEffect(() => {
    prepareRef.current = prepareRun;
  }, [prepareRun]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    // PERF-2026-07 (BUG #2): If the Electron process dies (OOM kill, crash,
    // user quit) while a dispatch is running, `complete()` never fires and the
    // dispatch stays in status='running' until the reaper eventually catches it
    // (up to 15min later). Use beforeunload to best-effort flush a `failed`
    // marker for every in-flight dispatch so the cloud side knows immediately.
    const flushRunningOnUnload = () => {
      for (const id of runningRef.current) {
        try {
          // sendBeacon is fire-and-forget; the body is minimal. navigator.sendBeacon
          // is the only network API that survives pagehide/beforeunload reliably.
          const url = apiUrl(`/api/dispatch/${id}/complete`);
          navigator.sendBeacon?.(url, JSON.stringify({ status: 'failed', error: 'runner_unloaded' }));
        } catch { /* best-effort — never block unload */ }
      }
    };
    window.addEventListener('beforeunload', flushRunningOnUnload);

    async function tick() {
      if (cancelled) return;
      let pending = [];
      try {
        pending = await remoteClient.pollPending();
      } catch {
        return;
      }
      for (const d of pending) {
        if (cancelled) break;
        if (runningRef.current.has(d.id)) continue;
        runningRef.current.add(d.id);
        runDispatch(d, { client: remoteClient, prepareRun: prepareRef.current })
          .catch(() => {})
          .finally(() => runningRef.current.delete(d.id));
      }
    }

    tick();
    const interval = setInterval(tick, pollMs);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('beforeunload', flushRunningOnUnload);
    };
  }, [enabled, pollMs]);

  return null;
}
